import { useEffect } from 'react';
import { Order_Enum, SortBy_Enum } from '@weave/schema-gen-ts/dist/schemas/schedule/quickfill/v2/quickfill.pb';
import { ScheduleV2 } from '@frontend/api-schedule-v2';
import { useTranslation } from '@frontend/i18n';
import { getFullName } from '@frontend/name';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';
import { CustomContact } from './CustomContact';
import { PersonNotesComponent } from './PersonNotesComponent';
import { PersonSearch } from './PersonSearch';
import { useManagePersonListContext } from './PersonSelectorContext';
import { personSelectorStyles } from './styles';
import { PatientSelectorScreenEnum } from './types';

export const PersonListContainer = () => {
  const { personSelectorScreen, shouldRenderNotes, setPersonSelectorScreen } = useManagePersonListContext();

  useEffect(() => {
    return () => {
      setPersonSelectorScreen(PatientSelectorScreenEnum.PERSON_SEARCH);
    };
  }, []);

  return (
    <>
      {personSelectorScreen === PatientSelectorScreenEnum.PERSON_SEARCH && <PersonSearch />}
      {personSelectorScreen === PatientSelectorScreenEnum.PERSON_NOTES && shouldRenderNotes && <PersonNotes />}
      {personSelectorScreen === PatientSelectorScreenEnum.CUSTOM_CONTACT && <CustomContact />}
    </>
  );
};

const PersonAlreadyExists = ({ name, onPrimaryClick }: { name: string; onPrimaryClick: () => void }) => {
  const { t } = useTranslation('schedule-quick-fill');

  return (
    <div css={{ padding: theme.spacing(1), position: 'relative' }}>
      <div
        css={[personSelectorStyles.header, { display: 'flex', justifyContent: 'space-between', alignItems: 'center' }]}
      >
        <Heading level={3}>{name}</Heading>
        <Button iconName='x' variant='secondary' onClick={onPrimaryClick} aria-label={t('Close')} />
      </div>
      <div css={personSelectorStyles.content}>
        <Text>{t('{{personName}} is already on the waitlist.', { personName: name })}</Text>
      </div>
      <footer css={{ padding: theme.spacing(2), display: 'flex', justifyContent: 'flex-end' }}>
        <Button onClick={onPrimaryClick}>{t('Done')}</Button>
      </footer>
    </div>
  );
};

const PersonNotes = () => {
  const {
    isLoading: isLoadingPerson,
    selectedPerson,
    locationId,
    closePopoverDialog,
    onAddToListHandler,
    addToListButtonTrackingId,
  } = useManagePersonListContext();

  const handleAddToList = (note: string) => {
    if (selectedPerson && onAddToListHandler) {
      onAddToListHandler({
        personId: selectedPerson.PersonID,
        notes: note,
        isNew: !existingPersonInfo?.personId,
      });
    }
  };

  const { data: waitlistData, isLoading: isLoadingWaitlist } = ScheduleV2.Queries.useListInfiniteQuery({
    request: {
      locationId: locationId,
      search: selectedPerson?.FirstName || selectedPerson?.LastName || '',
      sortBy: SortBy_Enum.CREATED_AT,
      order: Order_Enum.DESC,
    },
    options: {
      enabled: !!selectedPerson?.PersonID && !!locationId,
      cacheTime: 0,
      staleTime: 0,
      retry: false,
      useErrorBoundary: false,
    },
  });

  // TODO: we have issue with multi location quick fill waitlist data fetching,
  // Once it's resolved we can use the code below to get quick fill recipient info
  // and remove usage waitlistData
  // const { data: quickFillRecipient, isLoading: isLoadingQuickFillRecipient } = ScheduleV2.Queries.useEntryQuery({
  //   request: {
  //     personId: selectedPerson?.PersonID ?? '',
  //   },
  //   httpOptions: {
  //     headers: {
  //       [ScheduleDefaults.LOCATION_ID_HEADER]: locationId,
  //     },
  //   },
  //   options: {
  //     enabled: !!selectedPerson?.PersonID && !!locationId,
  //     cacheTime: 0,
  //     staleTime: 0,
  //     retry: false,
  //     useErrorBoundary: false,
  //   },
  // });

  const existingPersonInfo = waitlistData?.pages[0].data?.find((row) => row.personId === selectedPerson?.PersonID);

  if (existingPersonInfo?.person) {
    const fullName = selectedPerson ? getFullName(existingPersonInfo.person) : '';
    return <PersonAlreadyExists name={fullName} onPrimaryClick={closePopoverDialog} />;
  }

  return (
    <PersonNotesComponent
      isLoading={isLoadingPerson || isLoadingWaitlist}
      note={existingPersonInfo?.note ?? ''}
      heading={selectedPerson ? getFullName(selectedPerson) : ''}
      primaryButtonTrackingId={addToListButtonTrackingId}
      onPrimaryClick={handleAddToList}
      onSecondaryClick={closePopoverDialog}
    />
  );
};
